import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheckCircle, FiArrowRight, FiTarget, FiZap, FiUsers } = FiIcons;

const Welcome = () => {
  const { lang } = useLanguage();
  const t = translations[lang].welcome;
  const p = translations[lang].paths;

  const features = [
    { icon: FiTarget, text: t.step1 },
    { icon: FiZap, text: t.step2 },
    { icon: FiUsers, text: t.step3 }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 bg-gradient-to-b from-blue-50 to-white selection:bg-blue-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl shadow-blue-100/50 overflow-hidden border border-blue-100"
        >
          {/* Top Celebration Bar */}
          <div className="bg-blue-600 h-2 w-full" />
          
          <div className="p-8 md:p-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 12, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-8"
            >
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-600" />
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight"
            >
              {t.title}
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              {t.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mb-12"
            >
              <Link
                to={p.steps}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5"
              >
                {t.cta}
                <SafeIcon icon={FiArrowRight} className="ml-2 text-xl" />
              </Link>
            </motion.div>

            <div className="border-t border-slate-100 pt-12 text-left">
              <h2 className="text-xl font-bold text-slate-900 mb-8 flex items-center">
                <span className="w-1 h-6 bg-blue-600 rounded-full mr-3" />
                {t.nextSteps}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + (index * 0.1) }}
                    className="p-6 rounded-2xl bg-blue-50/50 border border-blue-100/50"
                  >
                    <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center mb-4 text-blue-600">
                      <SafeIcon icon={feature.icon} className="text-xl" />
                    </div>
                    <p className="text-slate-700 font-medium leading-snug">
                      {feature.text}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-500 italic">
            "Your journey of a thousand miles begins with a single step."
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Welcome;